import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/** Skeleton genérico (hero + blocos) nas páginas públicas. */
export function PublicPageSkeleton({
  className,
  dense = false,
}: {
  className?: string;
  dense?: boolean;
}) {
  return (
    <div
      className={cn('mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14', className)}
      aria-busy="true"
      aria-label="Carregando conteúdo"
    >
      <div className="mb-10 space-y-4">
        <Skeleton className="h-4 w-28 bg-white/10" />
        <Skeleton className="h-10 w-full max-w-md bg-white/15 sm:h-12" />
        <Skeleton className="h-4 w-full max-w-xl bg-white/10" />
        <Skeleton className="h-4 w-[66%] max-w-lg bg-white/10" />
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: dense ? 2 : 3 }).map((_, i) => (
          <div
            key={i}
            className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.04] p-5"
          >
            <Skeleton className="h-8 w-8 rounded-lg bg-white/15" />
            <Skeleton className="h-5 w-3/4 bg-white/12" />
            <Skeleton className="h-3 w-full bg-white/10" />
            <Skeleton className="h-3 w-[83%] bg-white/10" />
          </div>
        ))}
      </div>

      {!dense ? (
        <div className="space-y-3 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
          <Skeleton className="h-5 w-40 bg-white/12" />
          <Skeleton className="h-3 w-full bg-white/10" />
          <Skeleton className="h-3 w-full bg-white/10" />
          <Skeleton className="h-3 w-4/5 bg-white/10" />
          <Skeleton className="mt-4 h-24 w-full rounded-xl bg-white/10" />
        </div>
      ) : null}
    </div>
  );
}

/** Skeleton do detalhe de power — espelha hero (2 colunas) + secções brancas. */
export function PowerDetailPageSkeleton() {
  return (
    <div aria-busy="true" aria-label="Carregando power">
      {/* Hero navy */}
      <section className="relative overflow-hidden bg-[#030023] px-4 pb-14 pt-10 sm:px-6 sm:pb-16 sm:pt-12">
        <div className="relative z-10 mx-auto max-w-7xl">
          <Skeleton className="mb-8 h-3 w-40 bg-white/10" />

          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start lg:gap-10">
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <Skeleton className="h-16 w-16 shrink-0 rounded-2xl bg-white/12 sm:h-20 sm:w-20" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-8 w-48 bg-white/15 sm:h-9" />
                  <Skeleton className="h-5 w-28 rounded-full bg-white/10" />
                </div>
              </div>
              <Skeleton className="h-4 w-full max-w-lg bg-white/10" />
              <Skeleton className="h-4 w-[90%] max-w-md bg-white/10" />
              <Skeleton className="h-4 w-[70%] max-w-sm bg-white/10" />
              <div className="flex flex-wrap gap-2 pt-1">
                <Skeleton className="h-11 w-40 rounded-xl bg-white/12" />
                <Skeleton className="h-11 w-44 rounded-xl bg-white/10" />
              </div>
            </div>

            <div className="space-y-2.5 rounded-2xl border border-white/10 bg-white/[0.04] p-4 sm:p-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex h-[42px] items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.04] px-3.5"
                >
                  <Skeleton className="h-3 w-20 bg-white/10" />
                  <Skeleton className="h-3 w-24 bg-white/12" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Secções claras */}
      <div className="bg-white">
        <div className="mx-auto max-w-7xl space-y-10 px-6 py-12">
          <div className="space-y-3">
            <Skeleton className="h-6 w-48 bg-gray-200/80" />
            <Skeleton className="h-4 w-full max-w-2xl bg-gray-100" />
            <Skeleton className="h-4 w-[80%] max-w-xl bg-gray-100" />
          </div>

          <div>
            <Skeleton className="mb-5 h-6 w-40 bg-gray-200/80" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:grid-cols-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3"
                >
                  <Skeleton className="h-14 w-14 rounded-lg bg-gray-200/70" />
                  <Skeleton className="h-3 w-12 bg-gray-200/60" />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 border-t border-gray-100 pt-8">
            <Skeleton className="h-6 w-36 bg-gray-200/80" />
            <Skeleton className="h-24 w-full rounded-xl bg-gray-100" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Skeleton da listagem /powers — hero navy + grelha de cards. */
export function PowersListingSkeleton() {
  return (
    <div aria-busy="true" aria-label="Carregando catálogo de powers">
      <section className="relative overflow-hidden bg-[#030023] px-4 pb-12 pt-10 sm:px-6 sm:pb-14 sm:pt-12">
        <div className="relative z-10 mx-auto max-w-7xl space-y-8">
          <div className="mx-auto max-w-2xl space-y-3 text-center">
            <Skeleton className="mx-auto h-9 w-56 bg-white/15 sm:h-10" />
            <Skeleton className="mx-auto h-4 w-full max-w-md bg-white/10" />
          </div>
          <Skeleton className="mx-auto h-12 w-full max-w-xl rounded-2xl bg-white/10" />
          <div className="flex flex-wrap justify-center gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-28 rounded-2xl bg-white/10 sm:w-32" />
            ))}
          </div>
        </div>
      </section>

      <div className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-24 rounded-full bg-gray-200/80" />
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm"
              >
                <Skeleton className="aspect-[4/3] rounded-none bg-gray-100" />
                <div className="space-y-3 p-4">
                  <Skeleton className="h-4 w-2/3 bg-gray-100" />
                  <Skeleton className="h-3 w-full bg-gray-100" />
                  <Skeleton className="h-3 w-4/5 bg-gray-100" />
                  <div className="flex gap-2 pt-1">
                    <Skeleton className="h-8 flex-1 rounded-xl bg-gray-100" />
                    <Skeleton className="h-8 flex-1 rounded-xl bg-gray-100" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
